/**
 * useRevisionHistory Hook
 *
 * Fetches revision history for a note audit
 */

import { useMemo } from 'react';

import { useGetNoteAuditHistoryQuery } from '../store/api';
import type { AuditRevisionSummary, RevisionHistoryData } from '../types';

interface UseRevisionHistoryResult {
  /** Revision history */
  history: RevisionHistoryData | undefined;
  /** List of revisions (sorted newest first) */
  revisions: AuditRevisionSummary[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
}

export const useRevisionHistory = (noteId: string | undefined): UseRevisionHistoryResult => {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetNoteAuditHistoryQuery(
    { noteId: noteId! },
    { skip: !noteId },
  );

  // Sort revisions by revision number (newest first)
  const revisions = useMemo(() => {
    const list = data?.revisions || [];
    return [...list].sort((a, b) => b.revision - a.revision);
  }, [data]);

  return {
    history: data,
    revisions,
    isLoading,
    error: error ? 'Failed to load revision history' : null,
    refetch,
  };
};

export default useRevisionHistory;

